import fetch from 'isomorphic-fetch';

import { updateBulkFeatureFlagValues } from 'actions/featureFlag';
import * as analytics from 'utilities/analytics';
import * as logger from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';
import { store } from 'utilities/store';
import { getParam } from 'utilities/url';

const FLAG_BATCH_TIMEOUT = 150;
let batchedFlags = [];
let flagBatchTimeout;
let flagbatchTimeoutPromise;
let flagbatchTimeoutResolve;
let batchedExtraProperties = {};

export const getFlagValueForUser = async (
  flag,
  forceFetch = false,
  extraProperties = {}
) => {
  const flagUrlParam = getParam(flag);

  if (flagUrlParam) {
    let flagValue = flagUrlParam;

    if (flagValue === 'true') {
      flagValue = true;
    }

    if (flagValue === 'false') {
      flagValue = false;
    }

    return flagValue;
  }

  const currentValue = store?.getState().feature_flags[flag] || {};

  // if forceRefetch isn't true just return currentValue
  if (currentValue.fetched && !forceFetch) {
    return currentValue.value;
  }

  const { anonID } = analytics.getAnalyticsIds();

  batchedFlags.push(flag);
  batchedExtraProperties = {
    ...batchedExtraProperties,
    ...extraProperties,
  };

  if (!flagBatchTimeout) {
    flagbatchTimeoutPromise = new Promise(resolve => {
      flagbatchTimeoutResolve = resolve;
    });

    flagBatchTimeout = setTimeout(async () => {
      const flagsToSend = [...batchedFlags];
      const extraPropertiesToSend = {
        ...batchedExtraProperties,
      };

      // clear out timeouts as the fetch happens
      // we do not need to wait for the fetch to resolve
      // waiting causes there to be a race condition where a new flag might come in
      // get added to batchedFlags but the await call happens with an older version
      // this ensures that if a new flag request comes in during the await fetch it
      // gets added to the next round of calls
      batchedFlags = [];
      batchedExtraProperties = {};
      clearTimeout(flagBatchTimeout);
      flagBatchTimeout = null;

      flagbatchTimeoutResolve(
        await bulkFetchFlags(flagsToSend, anonID, extraPropertiesToSend)
      );
    }, FLAG_BATCH_TIMEOUT);
  }

  const flagsResult = (await flagbatchTimeoutPromise) || [];

  const matchingFlag = flagsResult.find(flagResult => flagResult.flag === flag);

  if (!matchingFlag || !matchingFlag.resultDetails) {
    return false;
  }

  return matchingFlag.resultDetails.result;
};

function bulkFetchFlags(flags, anonID, extraProperties = {}) {
  if (Array.isArray(flags)) {
    flags.forEach(flag => {
      if (typeof flag !== 'string') {
        logger.warning(
          new Error('invalid flag value'),
          {
            flag: JSON.stringify(flag),
          },
          {
            team: logger.LIFECYCLE_TEAM,
          }
        );
      }
    });
  }

  return fetch(
    `/v1/users/feature_flag/bulk?flags=${flags}&hash=${anonID}&extraProperties=${JSON.stringify(
      extraProperties
    )}`,
    {
      credentials: 'include',
    }
  )
    .then(resp => {
      if (resp.status >= 400) {
        throw new Error(`got a bad response for feature flag: ${resp.status}`);
      }

      return resp.json();
    })
    .then(flags => {
      if (store) {
        const user = store.getState().user;

        if (!user.is_logged_in) {
          const flagsObjToIdentify = flags.reduce(
            (flagAcc, { flag, resultDetails: { ineligible, result } }) => ({
              ...flagAcc,
              [flag.replace(/-/g, '_')]: ineligible ? 'ineligible' : result,
            }),
            {}
          );

          analytics.identify(null, flagsObjToIdentify);
        }

        store.dispatch(updateBulkFeatureFlagValues(flags));
      }

      return flags;
    })
    .catch(err => {
      sentry.logException(err, `Error in getting bulk feature flags: ${flags}`);

      return null;
    });
}
